(ns manage.product-selector-page.sections.product-selectors
  (:require ["@mui/material/Autocomplete$default" :as Autocomplete]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/Paper$default" :as Paper]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/TextField$default" :as TextField]
            ["react-hook-form" :as rhf]
            [cljs-bean.core :refer [->clj ->js]]
            [common.form :as form]
            [common.queries :refer [use-infinite-query use-query]]
            [common.resource.query.product-selector :as product-selector.q]
            [common.util :refer [spread-obj spread-props]]
            [common.widgets.form-field :refer [form-field]]
            [manage.product-selector-page.widgets :as widgets]
            [mate.core :as mc]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :refer [use-subscribe]]))

(def initial-product-selector-ref
  {:selector-ref-id nil
   :selector-limit nil
   :min-limit nil})

(defn- ->product-selector-options [^js data]
  (->> (->clj data)
       :pages
       (mapcat :items)))

(defhook use-selected-option [value]
  ;; capture the initial value only once
  (let [initial-value (uix/use-ref value)
        {:keys [advertiser-id]} (use-subscribe [:url-context])
        {product-selector :data} (use-query (product-selector.q/product-selector-query
                                             {:advertiser-id advertiser-id
                                              :product-selector-id @initial-value}))

        [selected-option set-selected-option] (uix/use-state {:label ""})]
    (uix/use-effect (fn []
                      (when product-selector
                        (set-selected-option
                         {:value (:id product-selector)
                          :label (:name product-selector)})))
                    [product-selector])
    {:selected-option selected-option
     :set-selected-option set-selected-option}))

(defui product-selector-id
  [{:keys [control field-name selected-ids]}]
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])]
    ($ form-field
       {:label "Product selector"}
       ($ rhf/Controller
          {:name (str field-name ".selector-ref-id")
           :control control
           :render (fn [^js props]
                     (let [{{:keys [error]} :fieldState
                            {:keys [onChange value]} :field} (->clj props)

                           {:keys [selected-option set-selected-option]} (use-selected-option value)

                           [input-value set-input-value] (uix/use-state "")
                           query (uix/use-memo (fn []
                                                 (spread-obj (product-selector.q/product-selectors-query
                                                              {:advertiser-id advertiser-id
                                                               :term input-value
                                                               :sort {:field "updated-at" :sort "desc"}
                                                               :page-size 10})
                                                             #js {:select ->product-selector-options}))
                                               [advertiser-id input-value])
                           {product-selectors :data isLoading :isLoading} (use-infinite-query query)
                           product-selector-options (uix/use-memo (fn []
                                                                    (->> product-selectors
                                                                         (map (fn [{:keys [name id]}]
                                                                                {:label name
                                                                                 :value id
                                                                                 :disabled (contains? selected-ids id)}))))
                                                                  [product-selectors selected-ids])]
                       ($ Autocomplete
                          {:size "small"
                           :value value
                           :onChange (fn [_ value]
                                       (set-selected-option value)
                                       (onChange (:value value)))
                           :options (to-array product-selector-options)
                           :noOptionsText (if isLoading
                                            "Loading product selectors…"
                                            "No product selectors found")
                           :filterOptions identity
                           :getOptionLabel (fn [option]
                                             (if (map? option)
                                               (:label option)
                                               (:label selected-option)))
                           :getOptionDisabled (fn [option]
                                                (:disabled option))
                           :renderOption (fn [props option state]
                                           ($ :div (js/Object.assign props #js {:key (.-index state)})
                                              (:label option)))
                           :onInputChange (fn [_ ^js new-input-value]
                                            (set-input-value new-input-value))
                           :renderInput (fn [^js props]
                                          ($ TextField
                                             (spread-props
                                              props
                                              #js {:helperText (:message error)
                                                   :error (boolean error)
                                                   :placeholder "Search or select a product selector…"})))})))}))))

(defui product-selector-limit
  [{:keys [control field-name]}]
  ($ form-field
     {:label "Max number of products"}
     ($ form/form-text-field
        {:control control
         :name (str field-name ".selector-limit")
         :on-change-fn js/parseInt
         :field-props {:type "number"
                       :inputProps #js {:min 1}}})))

(defui product-selector-min-limit
  [{:keys [control field-name]}]
  ($ form-field
     {:label "Min number of products"}
     ($ form/form-text-field
        {:control control
         :name (str field-name ".min-limit")
         :on-change-fn (fn [v] (let [parsed (js/parseInt v)]
                                 (when-not (js/isNaN parsed) parsed)))
         :field-props {:type "number"
                       :inputProps #js {:min 0}}})))

(defui product-selector-ref
  [{:keys [idx on-delete] :& props}]
  ($ Paper
     {:elevation 0
      :sx #js {:display "flex"
               :gap 1
               :px 2
               :py 1.5}}
     ($ Stack
        {:gap 1
         :sx #js {:flex 1}}
        ($ product-selector-id props)
        ($ product-selector-min-limit props)
        ($ product-selector-limit props))
     (when (> idx 1)
       ($ IconButton
          {:onClick on-delete
           :sx #js {:alignSelf "flex-start"}}
          ($ Icon "delete")))))

(defui edit []
  (let [{:keys [control]} (form/use-form-context)
        field-name "composite-config.selector-refs"
        {:keys [fields append remove]} (form/use-field-array #js {:control control
                                                                  :name field-name})
        selector-refs (rhf/useWatch #js {:control control :name field-name})
        selected-ids (->> (->clj selector-refs)
                          (keep :selector-ref-id)
                          set)]
    ($ Stack
       {:gap 2}
       (for [[idx field] (mc/seq-indexed fields)]
         ($ product-selector-ref
            {:key (.-id field)
             :idx idx
             :field-name (str field-name "[" idx "]")
             :control control
             :selected-ids selected-ids
             :on-delete #(remove idx)}))
       ($ Button
          {:variant "text"
           :startIcon ($ Icon "add")
           :onClick #(append (->js initial-product-selector-ref))
           :sx #js {:alignSelf "flex-start"}}
          "Add product selector"))))

(defui read-product-selector-ref [{:keys [selector-ref-id selector-limit min-limit]}]
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {product-selector :data} (use-query (product-selector.q/product-selector-query
                                             {:advertiser-id advertiser-id
                                              :product-selector-id selector-ref-id}))]
    ($ form-field
       {:label (:name product-selector)}
       ($ :span (str (when (and min-limit (pos? min-limit))
                       (str min-limit " products minimum, "))
                     selector-limit " products maximum")))))

(defui read []
  (let [{:keys [control]} (form/use-form-context)
        selector-refs (rhf/useWatch #js {:name "composite-config.selector-refs" :control control})]
    ($ Stack {:gap 2}
       (for [[idx selector-ref] (mc/seq-indexed selector-refs)]
         ($ read-product-selector-ref (assoc (->clj selector-ref) :key idx))))))

(defui product-selectors []
  (let [{:keys [mode]} (form/use-form-context)]
    ($ widgets/section-card
       {:title "Combine product selectors"
        :tooltip "Select two or more product selectors you’d like to combine into a single list."}
       (if (= mode :read)
         ($ read)
         ($ edit)))))
